import { useState, useMemo } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { authAPI } from "../lib/api";
import { Archive, Loader2, ArrowLeft, Check, X, MailCheck } from "lucide-react";

// Client-side password rule must match backend: 8+ chars, upper, lower, digit, symbol.
function checkPassword(pw) {
  return {
    length: (pw || "").length >= 8,
    upper: /[A-Z]/.test(pw || ""),
    lower: /[a-z]/.test(pw || ""),
    digit: /\d/.test(pw || ""),
    symbol: /[^A-Za-z0-9]/.test(pw || ""),
  };
}

function PasswordChecklist({ password }) {
  const checks = checkPassword(password);
  const items = [
    { key: "length", label: "At least 8 characters" },
    { key: "upper", label: "One uppercase letter" },
    { key: "lower", label: "One lowercase letter" },
    { key: "digit", label: "One number" },
    { key: "symbol", label: "One symbol (!@#$ etc.)" },
  ];
  return (
    <ul className="space-y-1 text-xs">
      {items.map((it) => {
        const ok = checks[it.key];
        return (
          <li key={it.key} className={`flex items-center gap-1.5 ${ok ? "text-green-600" : "text-muted-foreground"}`}>
            {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
            {it.label}
          </li>
        );
      })}
    </ul>
  );
}

function isPasswordStrong(pw) {
  const c = checkPassword(pw);
  return c.length && c.upper && c.lower && c.digit && c.symbol;
}

export default function AuthPage({ onAuth, onBack }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [mode, setMode] = useState("auth"); // "auth" | "forgot" | "registered"
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ name: "", email: "", password: "" });
  const [forgotEmail, setForgotEmail] = useState("");
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [resending, setResending] = useState(false);

  const registerPasswordOk = useMemo(
    () => isPasswordStrong(registerForm.password),
    [registerForm.password]
  );

  const clearMessages = () => {
    setError("");
    setInfo("");
    setUnverifiedEmail("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearMessages();
    try {
      const res = await authAPI.login(loginForm);
      localStorage.setItem("archiva_token", res.data.token);
      localStorage.setItem("archiva_user", JSON.stringify(res.data.user));
      onAuth(res.data.user);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail?.code === "email_not_verified") {
        setError(detail.message || "Please verify your email before signing in.");
        setUnverifiedEmail(loginForm.email);
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError("Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerPasswordOk) {
      setError("Please meet all password requirements.");
      return;
    }
    setLoading(true);
    clearMessages();
    try {
      await authAPI.register(registerForm);
      setRegisteredEmail(registerForm.email);
      setMode("registered");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic validation errors
        setError(detail.map((d) => d.msg).join(" "));
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError("Registration failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const target = unverifiedEmail || registeredEmail;
    if (!target) return;
    setResending(true);
    try {
      await authAPI.resendVerification(target);
      setInfo(`Verification email sent to ${target}. Check your inbox.`);
    } catch {
      setInfo(`Verification email sent to ${target}. Check your inbox.`); // don't leak
    } finally {
      setResending(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearMessages();
    try {
      await authAPI.forgotPassword(forgotEmail);
    } catch {
      // ignore, always show the same message
    } finally {
      setLoading(false);
      setInfo(
        `If an account exists for ${forgotEmail}, we've sent a password reset link. Check your inbox.`
      );
    }
  };

  // ----- "Account created, check your email" screen -----
  if (mode === "registered") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
        <div className="w-full max-w-md fade-in">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
                <Archive className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-4xl font-bold tracking-tight">Archiva</h1>
            </div>
          </div>
          <Card className="border border-border shadow-none">
            <CardContent className="pt-8 pb-6 text-center space-y-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <MailCheck className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold">Check your email</h2>
              <p className="text-sm text-muted-foreground">
                We've sent a verification link to <span className="font-medium text-foreground">{registeredEmail}</span>.
                Click it to activate your account and sign in.
              </p>
              {info && (
                <p className="text-xs text-green-700 bg-green-50 rounded-md p-2">{info}</p>
              )}
              <div className="flex flex-col gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={handleResendVerification}
                  disabled={resending}
                >
                  {resending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Resend verification email
                </Button>
                <Button variant="ghost" onClick={() => { setMode("auth"); clearMessages(); }}>
                  Back to sign in
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ----- Forgot password screen -----
  if (mode === "forgot") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
        <div className="w-full max-w-md fade-in">
          <button
            onClick={() => { setMode("auth"); clearMessages(); }}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to sign in
          </button>
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold tracking-tight">Reset your password</h1>
            <p className="text-muted-foreground text-sm mt-2">
              Enter your email and we'll send you a reset link.
            </p>
          </div>
          <Card className="border border-border shadow-none">
            <CardContent className="pt-6">
              {info ? (
                <div className="text-center space-y-4 py-4">
                  <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                    <MailCheck className="w-7 h-7 text-green-600" />
                  </div>
                  <p className="text-sm">{info}</p>
                  <Button variant="outline" onClick={() => { setMode("auth"); clearMessages(); }} className="w-full">
                    Back to sign in
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgot} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="you@example.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                    />
                  </div>
                  {error && (
                    <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">{error}</div>
                  )}
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Send reset link
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ----- Main sign-in / register screen -----
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
      <div className="w-full max-w-md fade-in">
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </button>
        )}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
              <Archive className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight" data-testid="app-title">Archiva</h1>
          </div>
          <p className="text-muted-foreground text-base">Your intelligent multimedia archive</p>
        </div>

        <Card className="border border-border shadow-none">
          <CardContent className="pt-6">
            <Tabs defaultValue="login" className="w-full" onValueChange={() => clearMessages()}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Create Account</TabsTrigger>
              </TabsList>

              {error && (
                <div className="mb-4 p-3 rounded-md bg-destructive/10 text-destructive text-sm" data-testid="auth-error">
                  {error}
                  {unverifiedEmail && (
                    <div className="mt-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleResendVerification}
                        disabled={resending}
                      >
                        {resending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                        Resend verification email
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {info && !error && (
                <div className="mb-4 p-3 rounded-md bg-green-50 text-green-800 text-sm">
                  {info}
                </div>
              )}

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="you@example.com"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">Password</Label>
                      <button
                        type="button"
                        onClick={() => { setMode("forgot"); clearMessages(); setForgotEmail(loginForm.email); }}
                        className="text-xs text-primary hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type="password"
                      placeholder="Enter your password"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Sign In
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Name</Label>
                    <Input
                      id="register-name"
                      data-testid="register-name-input"
                      placeholder="Your name"
                      value={registerForm.name}
                      onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      data-testid="register-email-input"
                      type="email"
                      placeholder="you@example.com"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <Input
                      id="register-password"
                      data-testid="register-password-input"
                      type="password"
                      placeholder="Create a strong password"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      required
                    />
                    {registerForm.password && (
                      <div className="rounded-md bg-gray-50 p-2">
                        <PasswordChecklist password={registerForm.password} />
                      </div>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading || !registerPasswordOk}
                    data-testid="register-submit-button"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Create Account
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
